import { useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, X, ShieldCheck, Camera } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fileToImageDataUrl } from "@/lib/hooks";
import { mirror } from "@/lib/syncStore";
import { SUPABASE_TABLES } from "@/lib/supabaseClient";
import { getPlatformSettings } from "@/lib/platformSettings";
import { initPaystackPayment, isPaystackConfigured } from "@/lib/paystack";

const PERSONAL_ID_TYPES = [
    { id: "passport",        label: "International Passport" },
    { id: "drivers_license", label: "Driver License" },
    { id: "nin",             label: "National ID / NIN" },
];

/**
 * Document-upload verification flow. Two variants by `:kind`:
 *  - personal: ID type + ID upload + selfie  (₦3,000)
 *  - business: CAC + owner ID + biz name + reg # (₦5,000)
 *
 * Persists to `neyra.verifications` (admin reviews) + mirrors to Supabase.
 * Verified badge only shows after admin approves.
 */
const VerifyDocs = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();
    const { kind } = useParams();
    const [searchParams] = useSearchParams();
    const isUpgrade = searchParams.get("upgraded") === "1";
    const isBusiness = kind === "business";
    const settings = getPlatformSettings();
    const fee = isBusiness
        ? settings.verificationFeeBusinessNgn || 5000
        : settings.verificationFeePersonalNgn || 3000;

    const [idType, setIdType] = useState("");
    const [idDoc, setIdDoc] = useState(null);
    const [selfie, setSelfie] = useState(null);
    const [cac, setCac] = useState(null);
    const [ownerId, setOwnerId] = useState(null);
    const [addressProof, setAddressProof] = useState(null);
    const [bizName, setBizName] = useState(user?.businessName || "");
    const [regNumber, setRegNumber] = useState("");
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [paymentError, setPaymentError] = useState(null);

    const idDocRef = useRef(null);
    const selfieRef = useRef(null);
    const cacRef = useRef(null);
    const ownerRef = useRef(null);
    const addressRef = useRef(null);

    const upload = async (file, setter) => {
        if (!file?.type?.startsWith("image/")) return;
        setBusy(true);
        try {
            const url = await fileToImageDataUrl(file, 1400, 0.8);
            setter(url);
        } finally {
            setBusy(false);
        }
    };

    const persistRecord = (paymentRef = null) => {
        const id = `vrf_${user.id}_${Date.now()}`;
        // Distinct request kind for business upgrade so admin treats it
        // differently (approval flips accountType + verifies the user).
        const requestKind = isUpgrade
            ? "business_upgrade"
            : isBusiness
                ? "business"
                : "personal";
        const record = {
            id,
            userId: user.id,
            kind: requestKind,
            status: "pending",
            paymentStatus: paymentRef ? "paid" : "unpaid",
            paymentRef,
            fee,
            createdAt: new Date().toISOString(),
            ...(isBusiness
                ? {
                      businessName: bizName.trim() || user?.businessName || null,
                      registrationNumber: regNumber.trim() || null,
                      cacDocument: cac,
                      ownerIdDocument: ownerId,
                      addressProofDocument: addressProof,
                      // Snapshot business profile fields so admin can review
                      // exactly what the user provided during upgrade.
                      pendingBusinessProfile: isUpgrade
                          ? {
                                businessName: user?.businessName || bizName.trim(),
                                businessCategory: user?.businessCategory || null,
                                businessAddress: user?.businessAddress || null,
                                businessPhone: user?.businessPhone || null,
                                businessEmail: user?.businessEmail || null,
                                businessDescription: user?.businessDescription || null,
                                businessLogo: user?.businessLogo || null,
                                location: user?.location || null,
                            }
                          : null,
                  }
                : { idType, idDocument: idDoc, selfie }),
        };

        try {
            const list = JSON.parse(localStorage.getItem("neyra.verifications") || "[]");
            list.unshift(record);
            localStorage.setItem("neyra.verifications", JSON.stringify(list));
        } catch {}
        try { mirror(SUPABASE_TABLES.VERIFICATIONS, record); } catch {}

        if (isUpgrade) {
            // Don't switch the account yet — admin must approve first.
            updateUser({ upgradeStatus: "pending", verified: false });
        } else {
            updateUser({ verificationStatus: "pending", verificationKind: requestKind, verified: false });
        }
        setSubmitted(true);
    };

    const submit = async () => {
        const e = {};
        if (isBusiness) {
            if (!bizName.trim() && !user?.businessName) e.bizName = "Business name is required";
            if (!cac) e.cac = "Upload your CAC / business certificate";
            if (!ownerId) e.ownerId = "Upload your personal ID";
            if (!addressProof) e.addressProof = "Upload an address proof (utility bill)";
        } else {
            if (!idType) e.idType = "Choose an ID type";
            if (!idDoc) e.idDoc = "Upload a photo of your ID";
            if (!selfie) e.selfie = "Take a selfie holding your ID";
        }
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }

        setPaymentError(null);
        const reference = `NEA-VRF-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

        // Paystack key missing → tell the user explicitly. No silent demo
        // submit (per Phase 3D rule). User can still proceed to admin
        // review only if a wallet path is wired (TODO) — for now we surface
        // a developer-friendly error so the issue is obvious.
        if (!isPaystackConfigured()) {
            setPaymentError(
                "Paystack key is not configured yet. Set REACT_APP_PAYSTACK_PUBLIC_KEY to accept verification payments."
            );
            return;
        }
        if (!user?.email) {
            setPaymentError(
                "We need your email to start a Paystack payment. Add an email to your profile and try again."
            );
            return;
        }

        setBusy(true);
        const res = await initPaystackPayment({
            email: user.email,
            amountNgn: fee,
            reference,
            metadata: {
                purpose: isUpgrade ? "business_upgrade" : "verification",
                kind: isBusiness ? "business" : "personal",
                userId: user.id,
            },
            onSuccess: (paidRef) => {
                setBusy(false);
                persistRecord(paidRef);
            },
            onClose: () => setBusy(false),
        });
        if (!res.ok) {
            setBusy(false);
            setPaymentError(
                res.reason === "no_amount"
                    ? "Invalid amount."
                    : "Could not start payment. Please try again."
            );
        }
    };

    if (submitted) {
        return (
            <div className="screen items-center justify-center px-6 text-center" data-testid="verify-docs-submitted">
                <span className="h-16 w-16 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-3">
                    <ShieldCheck size={26} />
                </span>
                <h2 className="font-display text-[20px] font-semibold">
                    {isUpgrade ? "Upgrade request submitted" : "Verification Pending"}
                </h2>
                <p className="mt-1.5 text-[13px] text-muted-foreground max-w-xs">
                    {isUpgrade
                        ? "Your business upgrade request has been submitted successfully. Our admin team will review your business information and documents. Once approved, your personal profile will automatically become a business profile."
                        : "Your verification has been submitted. Our admin team is reviewing your documents. You'll be notified once a decision is made."}
                </p>
                <button
                    type="button"
                    data-testid="verify-docs-submitted-back"
                    onClick={() => navigate("/profile", { replace: true })}
                    className="mt-6 h-12 px-6 rounded-full text-primary-foreground font-medium text-[14px]"
                    style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    Back to profile
                </button>
            </div>
        );
    }

    return (
        <div className="screen relative" data-testid={`verify-docs-${isBusiness ? "business" : "personal"}-screen`}>
            <header className="px-4 pt-[max(14px,env(safe-area-inset-top))] pb-3 border-b border-border/60 flex items-center justify-between">
                <button onClick={() => navigate(-1)} data-testid="verify-docs-back" className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-muted">
                    <ArrowLeft size={20} />
                </button>
                <h1 className="font-display text-[16px] font-semibold">
                    {isBusiness ? "Business verification" : "Personal verification"}
                </h1>
                <span className="h-10 w-10" />
            </header>

            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5 space-y-5">
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
                    <div className="flex items-center gap-3">
                        <span className="h-10 w-10 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                            <ShieldCheck size={18} />
                        </span>
                        <div>
                            <div className="font-display text-[14px] font-semibold">Get your verified badge</div>
                            <div className="text-[11.5px] text-muted-foreground">
                                One-time fee · ₦{Number(fee).toLocaleString()} · Reviewed by Neyra admin
                            </div>
                        </div>
                    </div>
                </div>

                {!isPaystackConfigured() && (
                    <div
                        data-testid="verify-docs-paystack-warning"
                        className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3.5 text-[12px] text-amber-700 dark:text-amber-300"
                    >
                        Paystack key is not configured yet. Set{" "}
                        <code className="font-mono text-[11.5px]">REACT_APP_PAYSTACK_PUBLIC_KEY</code>{" "}
                        to accept payments.
                    </div>
                )}

                {!isBusiness && (
                    <>
                        <section data-testid="verify-docs-id-type">
                            <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">ID type</span>
                            <div className="space-y-2">
                                {PERSONAL_ID_TYPES.map((t) => {
                                    const active = idType === t.id;
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            data-testid={`verify-docs-id-type-${t.id}`}
                                            onClick={() => { setIdType(t.id); if (errors.idType) setErrors({ ...errors, idType: null }); }}
                                            className={[
                                                "w-full flex items-center gap-3 p-3.5 rounded-2xl border text-left transition-all",
                                                active ? "border-primary bg-primary/5" : "border-border bg-secondary hover:bg-muted",
                                            ].join(" ")}
                                        >
                                            <span className={[
                                                "h-5 w-5 rounded-full border-2 flex items-center justify-center shrink-0",
                                                active ? "border-primary" : "border-muted-foreground/40",
                                            ].join(" ")}>
                                                {active && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                                            </span>
                                            <span className="text-[14px] font-medium">{t.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.idType && <p data-testid="verify-docs-id-type-error" className="text-[12px] text-destructive mt-1.5 ml-1">{errors.idType}</p>}
                        </section>

                        <UploadCard
                            testId="verify-docs-id-doc"
                            label="Upload ID document"
                            caption="Front of your ID — clear, all four corners visible"
                            value={idDoc} onPick={() => idDocRef.current?.click()} onClear={() => setIdDoc(null)} error={errors.idDoc}
                            inputRef={idDocRef} onChange={(f) => upload(f, setIdDoc)}
                        />
                        <UploadCard
                            testId="verify-docs-selfie"
                            label="Selfie holding your ID"
                            caption="Hold the ID next to your face — both must be readable"
                            value={selfie} onPick={() => selfieRef.current?.click()} onClear={() => setSelfie(null)} error={errors.selfie}
                            inputRef={selfieRef} onChange={(f) => upload(f, setSelfie)}
                            captureSelfie
                        />
                    </>
                )}

                {isBusiness && (
                    <>
                        <Field label="Business name" error={errors.bizName} testId="verify-docs-biz-name">
                            <input
                                data-testid="verify-docs-biz-name"
                                value={bizName}
                                onChange={(e) => setBizName(e.target.value)}
                                placeholder="Registered name"
                                className="flex-1 bg-transparent outline-none text-[15px]"
                            />
                        </Field>
                        <Field label="Registration number (optional)" testId="verify-docs-reg-number">
                            <input
                                data-testid="verify-docs-reg-number"
                                value={regNumber}
                                onChange={(e) => setRegNumber(e.target.value)}
                                placeholder="RC123456 / BN123456"
                                className="flex-1 bg-transparent outline-none text-[15px]"
                            />
                        </Field>
                        <UploadCard
                            testId="verify-docs-cac"
                            label="CAC / Business certificate"
                            caption="A clear photo or scan of your CAC certificate"
                            value={cac} onPick={() => cacRef.current?.click()} onClear={() => setCac(null)} error={errors.cac}
                            inputRef={cacRef} onChange={(f) => upload(f, setCac)}
                        />
                        <UploadCard
                            testId="verify-docs-owner-id"
                            label="Owner's personal ID"
                            caption="Passport, Driver License or NIN of the business owner"
                            value={ownerId} onPick={() => ownerRef.current?.click()} onClear={() => setOwnerId(null)} error={errors.ownerId}
                            inputRef={ownerRef} onChange={(f) => upload(f, setOwnerId)}
                        />
                        <UploadCard
                            testId="verify-docs-address-proof"
                            label="Address proof"
                            caption="Recent utility bill or official letter showing your business address"
                            value={addressProof}
                            onPick={() => addressRef.current?.click()}
                            onClear={() => setAddressProof(null)}
                            error={errors.addressProof}
                            inputRef={addressRef}
                            onChange={(f) => upload(f, setAddressProof)}
                        />
                    </>
                )}
            </div>

            <div className="px-5 pt-3 pb-[max(20px,env(safe-area-inset-bottom))] border-t border-border/60 bg-background space-y-2">
                {paymentError && (
                    <p
                        data-testid="verify-docs-payment-error"
                        className="text-[12px] text-destructive font-medium text-center"
                    >
                        {paymentError}
                    </p>
                )}
                <button
                    data-testid="verify-docs-submit"
                    onClick={submit}
                    disabled={busy}
                    className="w-full h-14 rounded-2xl text-primary-foreground font-medium text-[15px] active:scale-[0.98] disabled:opacity-60"
                    style={{
                        background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))",
                        boxShadow: "0 10px 28px -10px hsl(var(--primary) / 0.55)",
                    }}
                >
                    Pay ₦{Number(fee).toLocaleString()} & submit
                </button>
                {isBusiness && !isUpgrade && (
                    <button
                        type="button"
                        data-testid="verify-docs-complete-later"
                        onClick={() => {
                            // verification_status = none → user can return any time
                            // and the profile will still show "Get Verified".
                            updateUser({ verificationStatus: "none", verified: false });
                            navigate("/profile", { replace: true });
                        }}
                        className="w-full h-12 rounded-2xl bg-secondary text-secondary-foreground text-[13.5px] font-medium hover:bg-muted active:scale-[0.98]"
                    >
                        Complete verification later
                    </button>
                )}
                <p className="text-[11px] text-muted-foreground text-center mt-1">
                    Secure checkout via Paystack · Refunded if rejected.
                </p>
            </div>
        </div>
    );
};

const UploadCard = ({ testId, label, caption, value, onPick, onClear, error, inputRef, onChange, captureSelfie }) => (
    <section data-testid={testId}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        {value ? (
            <div className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-border bg-secondary" data-testid={`${testId}-preview`}>
                <img src={value} alt="" className="h-full w-full object-cover" />
                <button
                    type="button"
                    data-testid={`${testId}-clear`}
                    onClick={onClear}
                    className="absolute top-2 right-2 h-8 w-8 rounded-full bg-background/95 border border-border flex items-center justify-center text-destructive"
                >
                    <X size={14} />
                </button>
            </div>
        ) : (
            <button
                type="button"
                data-testid={`${testId}-pick`}
                onClick={onPick}
                className={[
                    "w-full aspect-[16/10] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-muted-foreground transition-colors",
                    error ? "border-destructive bg-destructive/5 text-destructive" : "border-border bg-secondary hover:border-primary hover:text-primary",
                ].join(" ")}
            >
                {captureSelfie ? <Camera size={22} /> : <ImagePlus size={22} />}
                <span className="text-[12px] font-medium">{caption}</span>
            </button>
        )}
        <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture={captureSelfie ? "user" : undefined}
            className="hidden"
            data-testid={`${testId}-input`}
            onChange={(e) => onChange(e.target.files?.[0])}
        />
        {error && <p data-testid={`${testId}-error`} className="text-[12px] text-destructive mt-1.5 ml-1">{error}</p>}
    </section>
);

const Field = ({ label, error, testId, children }) => (
    <label className="block" data-testid={`${testId}-field`}>
        <span className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">{label}</span>
        <span className={[
            "flex items-center gap-2 h-14 px-4 rounded-2xl border transition-colors",
            error ? "border-destructive bg-destructive/5" : "border-border bg-secondary focus-within:bg-background focus-within:border-primary",
        ].join(" ")}>
            {children}
        </span>
        {error && <span className="block text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>{error}</span>}
    </label>
);

export default VerifyDocs;
